import type { CommentsPostCard_PostData } from '../fragments/CommentsPostCard_Post'
import type { Service } from '../graphqlEnums'

export type ExampleComment = {
  id: string
  service: Service
  networkName: string
  authorName: string
  minutesAgo: number
  text: string
  likingIcon: 'thumbsUp' | 'heart' | null
  replyCharacterLimit: number
  suggestions: string[]
  sourcePost: {
    text: string
    minutesAgo: number
  }
}

export const EXAMPLE_REPLY_ACCOUNT_NAME = 'Your account'

export const EXAMPLE_COMMENTS: ExampleComment[] = [
  {
    id: 'example-comment-instagram',
    service: 'instagram',
    networkName: 'Instagram',
    authorName: 'maya.makes',
    minutesAgo: 12,
    text: 'Obsessed with this! Saving it for my next content day 🙌',
    likingIcon: null,
    replyCharacterLimit: 2200,
    suggestions: [
      'Thank you so much! Hope your content day goes well 🙌',
      'Love that, enjoy!',
    ],
    sourcePost: {
      text: '5 quick ways to plan a week of content in one afternoon ✨ Save this for your next batch day.',
      minutesAgo: 1440,
    },
  },
  {
    id: 'example-comment-linkedin',
    service: 'linkedin',
    networkName: 'LinkedIn',
    authorName: 'Daniel Ortiz',
    minutesAgo: 64,
    text: 'This is such a practical breakdown. Sharing it with our team today.',
    likingIcon: 'thumbsUp',
    replyCharacterLimit: 1250,
    suggestions: [
      'Thanks for sharing it with your team, Daniel!',
      'Glad it was useful. Let us know how it goes!',
    ],
    sourcePost: {
      text: 'How we cut our weekly social reporting from three hours to 30 minutes, step by step.',
      minutesAgo: 2880,
    },
  },
  {
    id: 'example-comment-facebook',
    service: 'facebook',
    networkName: 'Facebook',
    authorName: 'Priya Raman',
    minutesAgo: 185,
    text: 'We tried the batching tip last week and it saved us hours. Thank you!',
    likingIcon: 'thumbsUp',
    replyCharacterLimit: 8000,
    suggestions: [
      'That’s so great to hear, Priya!',
      'Hours saved is the best kind of feedback. Thank you!',
    ],
    sourcePost: {
      text: 'Our favourite time-saver this month: batch your posts. Write a week of updates in one sitting, then schedule them all at once.',
      minutesAgo: 4320,
    },
  },
  {
    id: 'example-comment-threads',
    service: 'threads',
    networkName: 'Threads',
    authorName: 'sam.builds',
    minutesAgo: 320,
    text: 'Love how consistent you’ve been lately. Keep them coming!',
    likingIcon: null,
    replyCharacterLimit: 500,
    suggestions: ['Thank you! More on the way 💪', 'Appreciate you, Sam!'],
    sourcePost: {
      text: 'Day 30 of posting every day. Here’s what changed 👇',
      minutesAgo: 1440,
    },
  },
  {
    id: 'example-comment-tiktok',
    service: 'tiktok',
    networkName: 'TikTok',
    authorName: 'lena.in.motion',
    minutesAgo: 1500,
    text: 'Okay this was genuinely helpful 😂 part 2 please?',
    likingIcon: 'heart',
    replyCharacterLimit: 150,
    suggestions: ['Part 2 is coming soon!', 'So glad it helped 😄'],
    sourcePost: {
      text: '3 editing tricks that make short videos feel twice as fast 🎬 Part 1',
      minutesAgo: 4320,
    },
  },
  {
    id: 'example-comment-youtube',
    service: 'youtube',
    networkName: 'YouTube',
    authorName: 'Chris Adeyemi',
    minutesAgo: 2900,
    text: 'Clear, quick, and actually useful. Subscribed ❤️',
    likingIcon: 'thumbsUp',
    replyCharacterLimit: 10000,
    suggestions: ['Welcome aboard, Chris! ❤️', 'Thanks for subscribing!'],
    sourcePost: {
      text: 'Schedule a month of social posts in 10 minutes | Quick tutorial',
      minutesAgo: 10080,
    },
  },
]

export function getExampleSourcePost(
  comment: ExampleComment,
  now: number,
): CommentsPostCard_PostData {
  const channelId = `${comment.id}-channel`
  return {
    __typename: 'Post',
    id: `${comment.id}-post`,
    status: 'sent',
    tags: [],
    metadata: null,
    assets: [],
    dueAt: new Date(now - comment.sourcePost.minutesAgo * 60_000).toISOString(),
    schedulingType: 'automatic',
    isCustomScheduled: false,
    isPinned: false,
    notificationStatus: null,
    text: comment.sourcePost.text,
    channelId,
    channelService: comment.service,
    externalLink: null,
    channel: {
      id: channelId,
      service: comment.service,
      serviceId: channelId,
      type: 'business',
      name: EXAMPLE_REPLY_ACCOUNT_NAME,
      displayName: EXAMPLE_REPLY_ACCOUNT_NAME,
    },
  }
}
